import { useEffect, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { getLineArena } from '../../data/arenas';
import { PushSim } from '../../engine/pushSim';
import { LineSim } from '../../engine/lineSim';
import { MazeSim } from '../../engine/mazeSim';
import type { RobotValues, RulePack, SimResult, SimTick } from '../../types';

interface Props {
  comp: Competition;
  robot: RobotValues;
  rulePack: RulePack;
  /** Selected arena id, for competitions that offer more than one track (line follower). */
  arenaId?: string;
  onResult: (result: SimResult) => void;
}

type SimInstance = PushSim | LineSim | MazeSim;

export default function SimulateTab({ comp, robot, rulePack, arenaId, onResult }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simRef = useRef<SimInstance | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState<SimTick | null>(null);

  useEffect(() => {
    return () => {
      simRef.current?.dispose();
      simRef.current = null;
    };
  }, []);

  const start = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    simRef.current?.dispose();
    setTick(null);
    setLoading(true);
    setRunning(true);

    const cb = {
      onTick: (t: SimTick) => setTick(t),
      onEnd: (r: SimResult) => {
        setRunning(false);
        onResult(r);
      },
    };

    let sim: SimInstance;
    if (comp.id === 'sumo' || comp.id === 'battle') {
      sim = new PushSim(canvas, comp.id, rulePack, robot, cb);
    } else if (comp.id === 'line') {
      sim = new LineSim(canvas, rulePack, robot, getLineArena(arenaId), cb);
    } else {
      sim = new MazeSim(canvas, rulePack, robot, cb);
    }
    simRef.current = sim;
    try {
      await sim.start();
    } finally {
      setLoading(false);
    }
  };

  const stop = () => {
    simRef.current?.dispose();
    simRef.current = null;
    setRunning(false);
    setLoading(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
      <div className="space-y-3">
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-navy-950">
          <canvas ref={canvasRef} className="size-full touch-none" />
          {!running && !loading && (
            <div className="absolute inset-0 grid place-items-center bg-navy-950/75 text-center">
              <p className="px-6 text-sm text-slate-400">
                Press “Start simulation” to load{' '}
                {comp.id === 'line' ? getLineArena(arenaId).name : comp.arenaName} in real-time physics.
              </p>
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 grid place-items-center bg-navy-950/75">
              <p className="text-sm text-slate-400">Loading physics engine…</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!running ? (
            <button
              type="button"
              onClick={start}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition ${comp.button}`}
            >
              <Play className="size-4" />
              Start simulation
            </button>
          ) : (
            <button
              type="button"
              onClick={stop}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-5 py-2.5 text-sm font-bold text-slate-200 ring-1 ring-white/10 transition hover:bg-white/15"
            >
              <Square className="size-4" />
              Stop
            </button>
          )}
          {tick && <span className="text-sm font-semibold tabular-nums text-slate-400">t = {tick.timeSec.toFixed(1)}s</span>}
        </div>
        <p className="text-xs text-slate-500">
          Drag to orbit the camera, scroll to zoom. The robot drives itself based on your Build tab settings
          and strategy.
        </p>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Live sensor HUD</h3>
        {tick ? (
          <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className={`text-sm font-extrabold ${comp.text}`}>{tick.status}</div>
            <dl className="space-y-1.5 text-xs">
              {Object.entries(tick.sensors).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-right font-mono text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-slate-500">
            Run the simulation to see live sensor readings, status, and timing here.
          </p>
        )}
      </div>
    </div>
  );
}
